import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  CreateInvestmentValuationRequestBody,
  FinancialClass,
  InvestmentFinancialMetricsResponse,
  InvestmentValuationDTO,
  InvestmentValuationListResponse,
  UpdateInvestmentValuationRequestBody,
} from '@inversor-hub/types';
import apiClient from '@/lib/api/apiClient';
import { INVESTMENT_TRANSACTIONS_KEY } from '@/lib/api/investment-transactions';

export const INVESTMENT_FINANCIAL_METRICS_KEY = 'investment-financial-metrics';
export const INVESTMENT_VALUATIONS_KEY = 'investment-valuations';

export function useGetInvestmentFinancialMetrics(investmentId?: string, year?: number) {
  return useQuery({
    queryKey: [INVESTMENT_FINANCIAL_METRICS_KEY, investmentId, year],
    queryFn: async () =>
      apiClient.get<InvestmentFinancialMetricsResponse>(
        `/investments/${investmentId}/financial-metrics?year=${year}`
      ),
    select: (response) => response.data.data,
    enabled: !!investmentId && !!year,
    placeholderData: keepPreviousData,
  });
}

export function useListInvestmentValuations(investmentId?: string) {
  return useQuery({
    queryKey: [INVESTMENT_VALUATIONS_KEY, investmentId],
    queryFn: async () =>
      apiClient.get<InvestmentValuationListResponse>(`/investments/${investmentId}/valuations`),
    select: (response) => response.data.data,
    enabled: !!investmentId,
  });
}

function useValuationInvalidation(investmentId: string) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [INVESTMENT_VALUATIONS_KEY, investmentId] });
    queryClient.invalidateQueries({ queryKey: [INVESTMENT_FINANCIAL_METRICS_KEY, investmentId] });
  };
}

export function useCreateInvestmentValuation(investmentId: string) {
  const invalidate = useValuationInvalidation(investmentId);

  return useMutation({
    mutationFn: async (data: CreateInvestmentValuationRequestBody) => {
      const response = await apiClient.post<
        unknown,
        AxiosResponse<{ data: InvestmentValuationDTO }>,
        CreateInvestmentValuationRequestBody
      >(`/investments/${investmentId}/valuations`, data);
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateInvestmentValuation(investmentId: string) {
  const invalidate = useValuationInvalidation(investmentId);

  return useMutation({
    mutationFn: async ({
      valuationId,
      data,
    }: {
      valuationId: string;
      data: UpdateInvestmentValuationRequestBody;
    }) => {
      const response = await apiClient.patch<
        unknown,
        AxiosResponse<{ data: InvestmentValuationDTO }>,
        UpdateInvestmentValuationRequestBody
      >(`/investments/${investmentId}/valuations/${valuationId}`, data);
      return response.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteInvestmentValuation(investmentId: string) {
  const invalidate = useValuationInvalidation(investmentId);

  return useMutation({
    mutationFn: async (valuationId: string) => {
      await apiClient.delete(`/investments/${investmentId}/valuations/${valuationId}`);
    },
    onSuccess: invalidate,
  });
}

export function useUpdateLineFinancialClass(investmentId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      lineId,
      financialClass,
    }: {
      transactionId: string;
      lineId: string;
      financialClass: FinancialClass;
    }) => {
      await apiClient.patch<unknown, AxiosResponse<void>, { financialClass: FinancialClass }>(
        `/investments/${investmentId}/transactions/${transactionId}/lines/${lineId}/financial-classification`,
        { financialClass }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_TRANSACTIONS_KEY, investmentId] });
      queryClient.invalidateQueries({ queryKey: [INVESTMENT_FINANCIAL_METRICS_KEY, investmentId] });
    },
  });
}
